import type { SyntheticEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortableLayers } from '@/common/presentation/components/layers/SortableLayers';
import { priceBookLineItemIds, type PriceBookSheetView } from '@/document-render/domain/priceBook/buildPriceBookBySheetView';
import type { LiveDocumentEditingConfig } from '@/document-render/presentation/editing/quote/LiveDocumentEditingConfig';
import { PriceBookRateTable } from '../PriceBookRateTable';
import { HIDDEN_OPACITY, SHEET_HEADING_STYLE, SHEET_NOTE_STYLE, VISUALLY_HIDDEN_STYLE, selectionOutline } from '../priceBookSheetStyles';
import { EditableRateCell } from './EditableRateCell';
import { PriceBookActionsMenu } from './PriceBookActionsMenu';
import { PriceBookQuoteRateRow } from './PriceBookQuoteRateRow';
import { PriceBookReveal } from './PriceBookReveal';
import { priceBookSheetSelectionId } from './priceBookEditing';
import type { PriceBookReorder } from './usePriceBookSheetReorder';
import { requireSliceId, usePriceBookSheetReorder } from './usePriceBookSheetReorder';
import { useCanvasReveal } from './useCanvasReveal';

export interface PriceBookSheetChromeProps {
  /** The sheet as printed in this slice. */
  sheet: PriceBookSheetView;
  /** The whole sheet (every row) - the line-item reorder domain. */
  fullSheet: PriceBookSheetView;
  editing: LiveDocumentEditingConfig;
  selectedId: string | null;
  onSelect: (id: string) => void;
  sheetReorder: PriceBookReorder;
}

const stop = (event: SyntheticEvent) => event.stopPropagation();

/** A customised rate sheet: "⠿ Rate sheet" tab, ⋮ menu, editable name, reorderable line items. */
export function PriceBookSheetChrome({ sheet, fullSheet, editing, selectedId, onSelect, sheetReorder }: PriceBookSheetChromeProps) {
  const selectionId = priceBookSheetSelectionId(sheet.sheetId);
  const selected = selectedId === selectionId;
  const sortable = useSortable({ id: sheet.sheetId, disabled: !editing.onReorderPriceBookRows });
  const { revealed, handlers } = useCanvasReveal();
  const rowsById = new Map(fullSheet.subsections.flatMap((subsection) => subsection.rows).map((row) => [row.id, row.name]));
  const lineItems = priceBookLineItemIds(fullSheet).map((id) => ({ id, name: rowsById.get(id) ?? id }));
  const lineItemReorder = usePriceBookSheetReorder(lineItems, (ids) => editing.onReorderPriceBookLineItems?.(sheet.sheetId, ids));
  const sliceLineItems = lineItems.filter((item) => priceBookLineItemIds(sheet).includes(item.id));
  const reorderSheets = Boolean(editing.onReorderPriceBookRows);
  const duplicate = editing.onDuplicatePriceBookSheet;
  const remove = editing.onDeletePriceBookSheet;
  const editName = editing.onEditPriceBookSheetName;

  const select = (event: SyntheticEvent) => {
    event.stopPropagation();
    onSelect(selectionId);
  };

  return (
    // a11y: selection target wraps nested sheet buttons — LLAMS-610
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={sortable.setNodeRef}
      data-price-book-sheet-id={sheet.sheetId}
      data-price-book-selection-id={selectionId}
      data-selected={selected ? 'true' : undefined}
      onPointerDown={select}
      {...handlers}
      onFocus={(event) => {
        handlers.onFocus();
        select(event);
      }}
      style={{
        position: 'relative',
        opacity: sheet.dimmed ? HIDDEN_OPACITY : undefined,
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        ...selectionOutline(selected)
      }}
    >
      <PriceBookReveal revealed={revealed || selected} side="right">
        {reorderSheets ? (
          <button
            type="button"
            ref={sortable.setActivatorNodeRef}
            aria-label={`Reorder rate sheet ${sheet.sheetName}`}
            data-canvas-affordance="grip"
            {...sortable.attributes}
            {...sortable.listeners}
            onClick={stop}
            onDoubleClick={stop}
            style={{ cursor: 'grab' }}
          >
            ⠿ Rate sheet
          </button>
        ) : null}
        <PriceBookActionsMenu
          label={`More actions - rate sheet ${sheet.sheetName}`}
          actions={[
            { label: 'Duplicate', onSelect: duplicate ? () => duplicate(sheet.sheetId) : undefined },
            { label: 'Delete', onSelect: remove ? () => remove(sheet.sheetId) : undefined },
            {
              label: 'Move up',
              onSelect: reorderSheets ? () => sheetReorder.moveUp(sheet.sheetId) : undefined,
              disabled: !sheetReorder.canMoveUp(sheet.sheetId)
            },
            {
              label: 'Move down',
              onSelect: reorderSheets ? () => sheetReorder.moveDown(sheet.sheetId) : undefined,
              disabled: !sheetReorder.canMoveDown(sheet.sheetId)
            }
          ]}
        />
      </PriceBookReveal>
      <div style={SHEET_HEADING_STYLE}>
        {editName ? (
          <EditableRateCell
            label={`Edit rate sheet name ${sheet.sheetName}`}
            value={sheet.sheetName}
            displayText={sheet.heading}
            numeric={false}
            onCommit={(value) => editName(sheet.sheetId, value)}
          />
        ) : (
          sheet.heading
        )}
      </div>
      {sheet.flatNote ? <div style={SHEET_NOTE_STYLE}>{sheet.flatNote}</div> : null}
      <SortableLayers
        items={sliceLineItems}
        onReorder={(from, to) => lineItemReorder.moveById(requireSliceId(sliceLineItems, from), requireSliceId(sliceLineItems, to))}
      >
        {sheet.subsections.map((subsection) => (
          <PriceBookRateTable
            key={subsection.key}
            subsection={subsection}
            renderRow={(row) => (
              <PriceBookQuoteRateRow
                key={row.id}
                row={row}
                subsectionKey={subsection.key}
                editing={editing}
                selectedId={selectedId}
                onSelect={onSelect}
                onMoveUp={() => lineItemReorder.moveUp(row.id)}
                onMoveDown={() => lineItemReorder.moveDown(row.id)}
                canMoveUp={lineItemReorder.canMoveUp(row.id)}
                canMoveDown={lineItemReorder.canMoveDown(row.id)}
              />
            )}
          />
        ))}
      </SortableLayers>
      <div role="status" aria-live="polite" style={VISUALLY_HIDDEN_STYLE}>
        {lineItemReorder.announcement}
      </div>
    </div>
  );
}
